import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CachedImage } from '~/components/common/cached-image/cached-image';
import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

interface ProductDetailGalleryProps {
  images: string[];
  /** Product title — used as the base for image alt text. */
  title: string;
}

/**
 * Image gallery for the Product Detail panel: a main image with looping
 * prev/next controls, a live counter, and a synced thumbnail strip. `←`/`→`
 * navigate while mounted (i.e. while the panel is open). Resets to the first
 * image whenever the `images` set changes (product navigation).
 */
export function ProductDetailGallery({ images, title }: ProductDetailGalleryProps) {
  const { t } = useTranslation('onboarding');
  const [active, setActive] = useState(0);
  const thumbRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const count = images.length;

  // Note: the panel remounts this component per product (via `key`), so
  // `active` resets to 0 on product navigation without a reset effect.
  const go = useCallback(
    (next: number) => {
      if (count === 0) return;
      // Loop at both ends.
      setActive(((next % count) + count) % count);
    },
    [count],
  );

  // Keep the active thumbnail scrolled into view as selection changes.
  useEffect(() => {
    thumbRefs.current[active]?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [active]);

  // Arrow-key navigation while the gallery (panel) is mounted. Ignored when
  // the user is typing in a field so it never hijacks form input.
  useEffect(() => {
    if (count <= 1) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('input, textarea, [contenteditable="true"]')) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(active - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(active + 1);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [active, count, go]);

  if (count === 0) {
    // No imagery from the feed — CachedImage renders the placeholder emblem.
    return (
      <div className="bg-muted flex aspect-square w-full items-center justify-center rounded-xl">
        <CachedImage src="" alt="" className="h-16 w-16" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="bg-muted relative aspect-square w-full overflow-hidden rounded-xl">
        <CachedImage
          src={images[active]}
          alt={t('productOverview.detail.gallery.imageAlt', { title, index: active + 1 })}
          className="h-full w-full object-contain"
        />

        {count > 1 && (
          <>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t('productOverview.detail.gallery.previous')}
              onClick={() => {
                go(active - 1);
              }}
              className="bg-background/90 hover:bg-background absolute top-1/2 left-2 h-8 w-8 -translate-y-1/2 cursor-pointer rounded-full shadow-sm"
            >
              <ChevronLeft aria-hidden="true" className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t('productOverview.detail.gallery.next')}
              onClick={() => {
                go(active + 1);
              }}
              className="bg-background/90 hover:bg-background absolute top-1/2 right-2 h-8 w-8 -translate-y-1/2 cursor-pointer rounded-full shadow-sm"
            >
              <ChevronRight aria-hidden="true" className="h-4 w-4" />
            </Button>

            {/* Live counter — announces the current position to screen readers. */}
            <span
              role="status"
              aria-live="polite"
              className="bg-background/90 text-muted-foreground absolute right-2 bottom-2 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums"
            >
              {t('productOverview.detail.gallery.counter', { current: active + 1, total: count })}
            </span>
          </>
        )}
      </div>

      {count > 1 && (
        <div className="flex gap-2.5 overflow-x-auto pb-1">
          {images.map((src, i) => {
            const isActive = i === active;
            return (
              <button
                key={`${src}-${String(i)}`}
                type="button"
                ref={(el) => {
                  thumbRefs.current[i] = el;
                }}
                aria-label={t('productOverview.detail.gallery.thumbnail', { index: i + 1 })}
                aria-current={isActive}
                onClick={() => {
                  setActive(i);
                }}
                className={cn(
                  'bg-background h-16 w-16 shrink-0 cursor-pointer overflow-hidden rounded-xl border-2 transition-all',
                  isActive ? 'border-foreground p-0.5' : 'border-transparent',
                )}
              >
                <CachedImage
                  src={src}
                  alt=""
                  // Inactive thumbnails are desaturated so the active one reads
                  // as the current selection; hover restores colour as a cue.
                  className={cn(
                    'h-full w-full rounded-lg object-cover transition-[filter]',
                    !isActive && 'grayscale hover:grayscale-0',
                  )}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
